import { invoke } from '@tauri-apps/api/core';
import type { BlobStorage } from '@toeverything/infra';

interface BlobData {
  data: number[];
  mime: string;
}

async function initWorkspace(workspaceId: string): Promise<void> {
  return invoke('plugin:workspace-storage|init_workspace', { workspaceId });
}

async function blobGet(
  workspaceId: string,
  key: string
): Promise<BlobData | null> {
  return invoke('plugin:workspace-storage|blob_get', { workspaceId, key });
}

async function blobSet(
  workspaceId: string,
  key: string,
  data: number[],
  mime: string
): Promise<void> {
  return invoke('plugin:workspace-storage|blob_set', {
    workspaceId,
    key,
    data,
    mime,
  });
}

async function blobDelete(workspaceId: string, key: string): Promise<void> {
  return invoke('plugin:workspace-storage|blob_delete', { workspaceId, key });
}

async function blobList(workspaceId: string): Promise<string[]> {
  return invoke('plugin:workspace-storage|blob_list', { workspaceId });
}

export class SqliteBlobStorage implements BlobStorage {
  constructor(private readonly workspaceId: string) {}

  private initialized = false;

  private async ensureInitialized() {
    if (this.initialized) return;
    await initWorkspace(this.workspaceId);
    this.initialized = true;
  }

  name = 'sqlite';
  readonly = false;

  async get(key: string): Promise<Blob | null> {
    await this.ensureInitialized();
    const result = await blobGet(this.workspaceId, key);
    if (!result) return null;
    return new Blob([new Uint8Array(result.data)], { type: result.mime });
  }

  async set(key: string, value: Blob): Promise<string> {
    await this.ensureInitialized();
    const arrayBuffer = await value.arrayBuffer();
    const data = Array.from(new Uint8Array(arrayBuffer));
    await blobSet(this.workspaceId, key, data, value.type || 'application/octet-stream');
    return key;
  }

  async delete(key: string): Promise<void> {
    await this.ensureInitialized();
    await blobDelete(this.workspaceId, key);
  }

  async list(): Promise<string[]> {
    await this.ensureInitialized();
    return blobList(this.workspaceId);
  }
}
